import { PlayListHeading } from 'app/ui-components/typography/PlayListHeading.styles';
import styled, { css, keyframes } from 'styled-components';
import CONTENTO_ACCESSIBILITIES from '../../../../domain/ContentoAccessibilities';
import { device } from '../../../../ui-components/breakpoints';
import {
    fontWeightBold,
    fontWeightLight,
    iconArrowDown,
    iconArrowRight,
    iconAudioDescribed,
    iconBan,
    iconPointRight,
    iconSignInterpretation,
    playGrey10,
    playGrey15,
    playTransitionCubicEaseInOut,
    playTransitionEasing,
    regularWhite,
} from '../../../../ui-components/design-tokens';
import PlayButton from '../../../../ui-components/PlayButton/PlayButton';
import PlayImage from '../../../../ui-components/PlayImage/PlayImage';
import { PlayLinkDiscreet } from '../../../../ui-components/PlayLink.styles';
import Progressbar from '../../../../ui-components/Progressbar/Progressbar';
import { Heading5 } from '../../../../ui-components/typography/Heading.styles';
import { playFontIcon, playFontSize, regularOutline } from '../../../../ui-components/typography/helpers';
import PlayText from '../../../../ui-components/typography/PlayText/PlayText';
import { getPlayIcon, PlayFocus, VisuallyHidden } from '../../../../ui-components/utils.styles';

export const SectionContainer = styled.div`
    @media ${device.GteLarge} {
        margin-top: 35px;
    }
`;

export const Section = styled.section`
    margin-top: 25px;
    border-top: 1px solid ${playGrey15};
    padding-top: 25px;
`;

export const Header = styled(PlayListHeading)`
    margin-bottom: 35px;

    @media ${device.GteLarge} {
        margin-bottom: ${(props) => (props.expanded ? '50px' : '25px')};
    }
`;

export const SectionExpandArrow = styled.span`
    background-color: transparent;
    margin-right: 1em;

    &:after {
        ${playFontIcon(1.5, iconPointRight)}
        font-size: 1em;
        color: ${regularWhite};
        transition: transform 60ms ease-in-out;
        transform: rotate(${(props) => (props.expanded ? '-90deg' : '90deg')});
    }
`;

export const SectionHeaderLink = styled.a`
    display: block;
    color: ${regularWhite};
    text-decoration: none;

    &:hover {
        text-decoration: underline;
        text-underline-position: under;
    }

    &:focus {
        ${regularOutline(10)};

        &:active {
            outline: none;
        }
    }
`;

const fadeIn = keyframes`
    0% {
        opacity: 0;
    }

    100% {
        opacity: 1;
    }
`;

export const Content = styled.article`
    display: flex;
    flex-flow: row wrap;
    margin-bottom: 40px;
    position: relative;

    @media ${device.GteSmall} {
        margin-bottom: 60px;
    }

    @media ${device.GteLarge} {
        margin-bottom: 80px;
    }

    ${(props) =>
        props.selected &&
        css`
            &:before {
                content: '';
                position: absolute;
                background-color: ${playGrey10};
                width: calc(100% + 20px);
                height: calc(100% + 20px);
                top: -10px;
                left: -10px;
                opacity: 0;
                animation: ${fadeIn} 60ms ${playTransitionCubicEaseInOut} forwards;
            }

            @media ${device.GteSmall} {
                &:before {
                    width: calc(100% + 30px);
                    height: calc(100% + 30px);
                    top: -15px;
                    left: -15px;
                }
            }
        `}
`;

const imagePaddingRightSmall = '19px';
const imagePaddingRightLarge = '20px';
const imagePaddingRightFullscreen = '40px';

export const ImageContainer = styled.div`
    position: relative;
    width: calc(34% + 12px);
    align-self: flex-start;

    @media ${device.GteSmall} {
        width: calc(30% + ${imagePaddingRightSmall});
    }

    @media ${device.GteLarge} {
        width: calc(20% + ${imagePaddingRightLarge});
    }

    @media ${device.FullScreen} {
        width: calc(20% + ${imagePaddingRightFullscreen});
    }
`;

export const AssoiciatedContentImage = styled(PlayImage)`
    ${(props) =>
        props.onlyAvailableInSweden &&
        css`
            .no-sweden & {
                opacity: 1;
            }
        `};

    ${(props) => props.faded && 'opacity: 0.3;'}

    ${(props) =>
        props.onlyAvailableInSweden &&
        css`
            .no-sweden & :after {
                font-family: svt-icons;
                content: '${iconBan}';
                position: absolute;
                margin-top: -0.5em;
                margin-left: -0.5em;
                top: 50%;
                left: 50%;
                z-index: 10;
                text-align: center;
                color: ${regularWhite};
                color: rgba(255, 255, 255, 0.65);
                ${playFontSize(4.5)}
            }
        `}
`;

export const ImageLink = styled.a`
    display: block;
    text-decoration: none;
    padding-right: 12px;

    &:focus {
        outline: none;
    }

    ${AssoiciatedContentImage} {
        ${(props) =>
            !props.isUpcoming &&
            css`
                .touchevents & {
                    ${getPlayIcon(true)}
                }
            `}

        ${(props) =>
            props.shouldShowPlayIcon &&
            css`
                .no-touchevents.sweden & {
                    ${getPlayIcon(false)}
                }
            `}
    }

    @media ${device.GteSmall} {
        padding-right: ${imagePaddingRightSmall};
    }

    @media ${device.GteLarge} {
        padding-right: ${imagePaddingRightLarge};
    }

    @media ${device.FullScreen} {
        padding-right: ${imagePaddingRightFullscreen};
    }
`;

export const UpcomingOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    flex-flow: column wrap;
    width: calc(100% - 12px);
    height: 100%;
    justify-content: center;
    align-items: center;
    z-index: 1;
    transform: translate3d(0, 0, 0); /* fixes opacity bug in chrome when tabbing */

    @media ${device.GteSmall} {
        width: calc(calc(100% - ${imagePaddingRightSmall}));
    }

    @media ${device.GteLarge} {
        width: calc(calc(100% - ${imagePaddingRightLarge}));
    }

    @media ${device.FullScreen} {
        width: calc(calc(100% - ${imagePaddingRightFullscreen}));
    }

    ${(props) =>
        props.onlyAvailableInSweden &&
        css`
            .no-sweden & {
                display: none;
            }
        `};
`;

export const UpcomingOverlayText = styled.span`
    display: block;
    ${playFontSize(1.5)}
    text-transform: uppercase;
    color: ${regularWhite};
    line-height: 1.2;

    @media ${device.GteXsmall} {
        ${playFontSize(1.8)}
    }

    @media ${device.GteSmall} {
        ${playFontSize(2.4)}
    }

    @media ${device.GteMedium} {
        ${playFontSize(3)}
    }

    @media ${device.FullScreen} {
        ${playFontSize(4)}
    }

    font-weight: ${(props) => (props.bold ? fontWeightBold : fontWeightLight)};
`;

export const Information = styled.div`
    position: relative;
    width: calc(56% - 12px);

    @media ${device.GteSmall} {
        width: calc(${(props) => (props.isUpcoming ? '60%' : '70%')} - ${imagePaddingRightSmall});
    }

    @media ${device.GteLarge} {
        width: calc(${(props) => (props.isUpcoming ? '70%' : '80%')} - ${imagePaddingRightLarge});
    }

    @media ${device.FullScreen} {
        width: calc(${(props) => (props.isUpcoming ? '70%' : '80%')} - ${imagePaddingRightFullscreen});
    }
`;

export const ContentHeader = styled(Heading5)`
    margin-bottom: 5px;
`;

export const ContentSubheader = styled(PlayText)`
    @media ${device.GteSmall} {
        margin-bottom: 10px;
    }
`;

export const ContentLink = styled.a`
    color: ${regularWhite};
    text-decoration: none;
    ${PlayFocus}

    &:hover {
        text-decoration: underline;
        text-underline-position: under;
    }
`;

export const ContentDescription = styled(PlayText)`
    display: none;
    max-width: 680px;
    line-height: 1.4;

    @media ${device.GteSmall} {
        display: ${(props) => (props.expanded || !props.isUpcoming ? 'block' : 'none')};
    }
`;

export const MobileDescription = styled(PlayText)`
    display: ${(props) => (props.expanded ? 'block' : 'none')};
    margin-top: 15px;
    margin-bottom: 10px;
    position: relative;

    @media ${device.GteSmall} {
        display: none;
    }
`;

export const ExpandButton = styled.button`
    background-color: transparent;
    width: 10%;
    border: none;
    height: 65px;

    &:focus {
        border: none;
        outline: none;
    }

    &:after {
        ${playFontIcon(1.5, iconPointRight)}
        color: ${regularWhite};
        transition: transform 60ms ease-in-out;
        transform: rotate(${(props) => (props.expanded ? '-90deg' : '90deg')});
    }

    @media ${device.GteSmall} {
        display: ${(props) => (props.isUpcoming ? 'block' : 'none')}
    }
`;

const getIcon = (accessibility) => {
    if (accessibility === CONTENTO_ACCESSIBILITIES.SIGN_INTERPRETED) {
        return iconSignInterpretation;
    }

    if (accessibility === CONTENTO_ACCESSIBILITIES.AUDIO_DESCRIBED) {
        return iconAudioDescribed;
    }

    return null;
};

export const Accessibility = styled.span`
    &:before {
        ${(props) => playFontIcon(1.5, getIcon(props.accessibility))}
        font-size: 1.2em;
        vertical-align: top;
    }
`;

export const AccessibilityText = styled.span`
    ${VisuallyHidden}
`;

export const ShowMoreButton = styled(PlayButton)`
    display: block;
    width: 100%;

    @media ${device.GteSmall} {
        width: auto;
        margin: 0 auto;
    }
`;

export const ShowMoreIcon = styled.span`
    &:after {
        ${playFontIcon(1.5, iconArrowDown)}
        vertical-align: top;
        margin-right: 5px;
    }
`;

export const AssociatedMoreInfoLink = styled.a`
    ${PlayLinkDiscreet};
    ${PlayFocus}
    ${playFontSize(1.4)}
    font-weight: ${fontWeightBold};
    color: ${regularWhite};
    margin-top: 15px;
    display: block;

    @media ${device.GteSmall} {
        ${playFontSize(1.5)}
    }

    &:after {
        ${playFontIcon(1.3, iconArrowRight)}

        line-height: normal;
        text-decoration: none;
        margin-left: 5px;
        transition: transform 200ms ${playTransitionEasing};
        margin-top: -1px;
    }

    &:hover {
        text-decoration: underline;
        cursor: pointer;

        &:after {
            transform: translateX(4px);
        }
    }
`;

export const AssociatedProgressBar = styled(Progressbar)`
    padding: 0;

    & span {
        margin-top: 0;
    }
`;
